import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { UpdateFilterPackArgs, useFilterPack, useUpdateFilterPack } from '@/services/api/hooks/catalog';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { FunctionComponent, Suspense } from 'react';
import { toast } from 'sonner';

const DeleteIndividualProductFilterModal: FunctionComponent<
  ModalProps<{ productFilterId: string; filteredByMargin?: boolean }>
> = props => {
  const DeleteIndividualProductFilter = () => {
    const filterIdToRemove = props.props?.productFilterId;
    if (!filterIdToRemove) throw new Error('No product filter ID provided');

    const filterPackId = useCatalogueService(state => state.filterPackId);
    if (!filterPackId) throw new Error('Missing filter pack id');
    const [filterPack] = useFilterPack(filterPackId);
    const updateFilterPack = useUpdateFilterPack();

    const filterToRemove = filterPack.productFilters.find(filter => filter.id === filterIdToRemove);

    const onDelete = async () => {
      const updatedFilterPack: UpdateFilterPackArgs = {
        id: filterPack.id,
        globalMarginModifier: filterPack.globalMarginModifier,
        marginFilterIds: props.props?.filteredByMargin
          ? filterPack.marginFilters.filter(filter => filter.id !== filterIdToRemove).map(filter => filter.id)
          : filterPack.marginFilters.map(filter => filter.id),
        productFilterIds: props.props?.filteredByMargin
          ? filterPack.productFilters.map(filter => filter.id)
          : filterPack.productFilters.filter(filter => filter.id !== filterIdToRemove).map(filter => filter.id)
      };

      await updateFilterPack.mutateAsync(updatedFilterPack);
      props.close();
      toast.success(`Filter deleted`);
    };

    const errorObj = updateFilterPack.error as Error | undefined;
    const error: string = errorObj ? errorObj.message : '';

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Delete individual filter</h1>
        </Modal.Controls>

        <div className="space-y-3 mt-2">
          <p className="text-lg">
            Are you sure you want to delete the filter for{' '}
            <span className="font-medium">{filterToRemove?.daasCatalogItem?.name}?</span>
          </p>

          {error && <p className="text-red-500">{error}</p>}

          <div className="flex gap-2 justify-end">
            <Button className="flex-1" onClick={props.close}>
              Cancel
            </Button>
            <Button
              type="button"
              className="flex-1"
              variant={ButtonVariant.Destructive}
              loading={updateFilterPack.isLoading}
              onClick={onDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <DeleteIndividualProductFilter />
      </Suspense>
    </Modal>
  );
};

export default DeleteIndividualProductFilterModal;
